/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { FormEvent, FunctionComponent, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import ButtonLoading from "../../Button/ButtonLoading";
import { useNavigate } from "react-router-dom";
import powerOfAttorneyStyle from "./powerOfAttorneyStyle";
import PoaAgreementText from "../Common/PowerOfAttorney/PoaAgreementText";
import { useForm } from "../../../provider/Form/FormProvider";
import moment from "moment";
import Typography from "@mui/material/Typography";
import { getCaseLink } from "../../../services/productService";
import { Box } from "@mui/material";
import { getPowerOfAttorneyAuthorizationItems } from "../../../services/contractLaw/powerOfAttorneyInformationLists";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import FormSubmitProps from "../../../types/FormSubmitProps";
import MultipleSignatures from "../Common/Signature/MultipleSignatures";
import PowerOfAttorneySignatoryHighlightBox from "./PowerOfAttorneySignatoryHighlightBox";
import PowerOfAttorneyOutroHighlightBox from "./PowerOfAttorneyOutroHighlightBox";

type PowerOfAttorneyProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
  SignatoryHighlightBox?: FunctionComponent<{ product: AbstractCase }> | null;
  OutroHighlightBox?: FunctionComponent<{ product: AbstractCase }> | null;
};

export default function PowerOfAttorney({
  product,
  submitProps,
  SignatoryHighlightBox = PowerOfAttorneySignatoryHighlightBox,
  OutroHighlightBox = PowerOfAttorneyOutroHighlightBox,
}: PowerOfAttorneyProps) {
  const navigate = useNavigate();
  const { handleSubmit, handleChange, values } = useForm();

  if (!product.signatories || !product.additionalClients) {
    return null;
  }

  const { powerOfAttorney } = values;
  useEffect(() => {
    if (powerOfAttorney === null) {
      handleChange({ target: { name: "powerOfAttorney", value: moment().format("DD.MM.YYYY") } });
    }
  }, [powerOfAttorney]);

  const handlePoaSubmit = async (event: FormEvent<HTMLFormElement>) => {
    if (!submitProps.isMinimal) {
      await handleSubmit(event);
      return;
    }
    await handleSubmit(event, { preventRedirect: true });
    navigate(getCaseLink(product) + "/fall-vollstaendig");
  };

  const usePlural = product.additionalClients?.length > 0 || product.clientLegalRepresentativeAmount === 2;

  return (
    <Paper sx={powerOfAttorneyStyle.paper} elevation={4}>
      <Box component={"form"} sx={powerOfAttorneyStyle.applicationForm} id={"application-form"} onSubmit={handlePoaSubmit}>
        <Typography variant={"h4"} sx={powerOfAttorneyStyle.poaHeadline}>
          Vollmacht
        </Typography>
        <div>
          <p>
            Hiermit {usePlural ? "erteilen wir" : "erteile ich"} der Legalbird Rechtsanwälte Rechtsanwalts GmbH Vollmacht zu {usePlural ? "unserer" : "meiner"}{" "}
            Verteidigung und Vertretung, mit der Ermächtigung zur Bestellung von Unterbevollmächtigten.
          </p>
          <p>Die Vollmacht ermächtigt zu allen die Angelegenheit betreffenden Handlungen, insbesondere zur</p>
          <ul>
            {getPowerOfAttorneyAuthorizationItems(product).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Die Vollmacht erstreckt sich auch auf Nebenverfahren mit Ausnahme der Zwangsvollstreckung.</p>
          <p>
            {usePlural ? "Uns" : "Mir"} ist bekannt, dass mit der Vollmacht eine Beauftragung zu {usePlural ? "unserer" : "meiner"} Verteidigung und damit eine
            Anwaltsvergütung entsteht, deren Höhe sich nach den gesetzlichen Vorschriften richtet.
          </p>
          <PoaAgreementText lawFirmName={product.lawFirm.name} multipleClients={usePlural} />
        </div>
        {!!SignatoryHighlightBox && <SignatoryHighlightBox product={product} />}
        <MultipleSignatures
          product={product}
          signatories={product.signatories ? product.signatories.sort((a, b) => (a.id || 0) - (b.id || 0)) : [product.client]}
        />

        <Grid container>
          <Grid item xs={12}>
            <ButtonLoading
              color="primary"
              variant="contained"
              type="submit"
              fullWidth={true}
              disabled={product.signatories.some((signee) => !signee.signature)}
              isLoading={submitProps.isLoading}
            >
              Vollmacht erteilen und Fall prüfen lassen
            </ButtonLoading>
          </Grid>
        </Grid>
        {!!OutroHighlightBox && (
          <Box sx={{ paddingTop: 3 }}>
            <OutroHighlightBox product={product} />
          </Box>
        )}
      </Box>
    </Paper>
  );
}
